import { type ReactNode, useEffect, useState } from "react";
import type { Persistor } from "redux-persist";
import {
  type PublicRuntimeConfig,
  parsePublicRuntimeConfigJson,
} from "@/lib/config/publicRuntimeConfig";
import { createAppRouter, type AppRouter } from "@/router";
import { createAppStore } from "@/store/app/createAppStore";
import { setPublicRuntimeConfigInstance } from "@/store/app/publicRuntimeConfigInstance";
import type { AppStore } from "@/store/app/storeTypes";
import AppLoadingScreen from "@/components/organisms/appLoadingScreen";

const fetchPublicRuntimeConfig = async (): Promise<PublicRuntimeConfig> => {
  let res: Response;
  try {
    res = await fetch("/config");
  } catch {
    throw new Error(
      "Network error while loading /config. Ensure Express is running and you open the app URL described in the README.",
    );
  }
  if (!res.ok) {
    throw new Error(
      `GET /config failed with HTTP ${String(res.status)}. Ensure Express is running and the app is opened on the correct origin.`,
    );
  }
  const json: unknown = await res.json();
  if (
    typeof json === "object" &&
    json != null &&
    !("cognitoAuthority" in json)
  ) {
    throw new Error(
      "GET /config is missing Cognito fields. The Express dev server on port 3000 is likely stale — stop `npm run dev` completely and start it again.",
    );
  }
  return parsePublicRuntimeConfigJson(json);
};

export type ConfigReadyBundle = {
  publicRuntimeConfig: PublicRuntimeConfig;
  store: AppStore;
  persistor: Persistor;
  router: AppRouter;
};

type Props = {
  children: (bundle: ConfigReadyBundle) => ReactNode;
};

export const ConfigProvider = ({ children }: Props) => {
  const [bundle, setBundle] = useState<ConfigReadyBundle | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const publicRuntimeConfig = await fetchPublicRuntimeConfig();
        if (cancelled) return;
        setPublicRuntimeConfigInstance(publicRuntimeConfig);
        const { store, persistor } = createAppStore(publicRuntimeConfig);
        const router = createAppRouter(store);
        setBundle({ publicRuntimeConfig, store, persistor, router });
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load public runtime config", error);
        setLoadError(error instanceof Error ? error : new Error(String(error)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError !== null) {
    throw loadError;
  }

  if (bundle === null) {
    return <AppLoadingScreen />;
  }

  return children(bundle);
};
